import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EMPTY, Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { OauthResponse } from '../auth/auth.service';
import { BrandService } from './brand.service';
import { PlatformService } from './platform.service';
import { StorageService } from './storage.service';

/**
 * LINE 로그인 응답 인터페이스
 * LINE OAuth2 인증 후 받는 토큰 정보를 정의합니다.
 */
export interface LineLoginResponse {
  /** 액세스 토큰 */
  access_token: string;
  /** 토큰 만료 시간(초) */
  expires_in: number;
  /** ID 토큰 (OpenID Connect) */
  id_token: string;
  /** 리프레시 토큰 */
  refresh_token: string;
  /** 승인된 권한 범위 */
  scope: string;
  /** 토큰 타입 (Bearer) */
  token_type: string;
}

/**
 * LINE 사용자 프로필 인터페이스
 * LINE API를 통해 받아오는 사용자 정보를 정의합니다.
 */
export interface LineUserProfile {
  /** LINE 사용자 고유 ID */
  userId: string;
  /** 사용자 표시 이름 */
  displayName: string;
  /** 프로필 이미지 URL (선택) */
  pictureUrl?: string;
  /** 상태 메시지 (선택) */
  statusMessage?: string;
}

/**
 * LINE 로그인 서비스
 * LINE OAuth2 인증을 처리하는 서비스입니다.
 * 사용자 로그인, 콜백 처리, 프로필 정보 조회 등의 기능을 제공합니다.
 */
@Injectable({
  providedIn: 'root',
})
export class LineAuthService {
  /** LINE 로그인 후 리다이렉트 URI */
  private readonly LINE_REDIRECT_URI = `${globalThis.location?.origin ?? ''}/auth/line/callback`;

  constructor(
    private activatedRoute: ActivatedRoute,
    private http: HttpClient,
    private platformService: PlatformService,
    private storageService: StorageService,
    private brandService: BrandService,
  ) {}

  /**
   * LINE 로그인 프로세스를 시작합니다.
   * 사용자를 LINE 로그인 페이지로 리다이렉트합니다.
   */
  initiateLineLogin(redirectTo: string): void {
    this.http
      .get<{ url: string }>(`${environment.apiServerUrl}/auth/line/login`, {
        params: {
          redirectUri: `${this.LINE_REDIRECT_URI}?redirectTo=${redirectTo ?? '/home'}`,
        },
      })
      .subscribe(({ url }) => {
        window.location.href = url;
      });
  }

  /**
   * LINE 로그인 콜백을 처리합니다.
   * 인증 코드를 받아 액세스 토큰을 발급받고 사용자 프로필을 조회합니다.
   *
   * @param code LINE OAuth2 인증 코드
   * @returns 사용자 프로필 정보 Observable
   */
  handleCallback(code: string): Observable<OauthResponse> {
    if (this.platformService.isPlatformServer) {
      return EMPTY;
    }

    return this.getAccessToken(code);
  }

  /**
   * LINE 로그인 콜백을 처리합니다.
   * 인증 코드를 받아 사용자 프로필을 조회합니다.
   *
   * @param code LINE OAuth2 인증 코드
   * @returns 사용자 프로필 정보 Observable
   */
  private getAccessToken(code: string): Observable<OauthResponse> {
    return this.http
      .get<OauthResponse>(`${environment.apiServerUrl}/auth/line/callback`, {
        params: {
          code,
          brandId: this.brandService.brand.id!,
          redirectUri: `${this.LINE_REDIRECT_URI}?redirectTo=${this.activatedRoute.snapshot.queryParams['redirectTo']}`,
        },
      })
      .pipe(
        tap((oauthResponse) => {
          this.storageService.set('tokenType', oauthResponse.token_type);
          this.storageService.set('accessToken', oauthResponse.access_token);
          this.storageService.set('refreshToken', oauthResponse.refresh_token);
          this.storageService.set(
            'expireDttm',
            new Date(Date.now() + oauthResponse.expires_in * 1000),
          );

          /* if (this.activatedRoute.snapshot.queryParams['redirectTo']) {
            window.location.href = `${window.location.origin}${this.activatedRoute.snapshot.queryParams['redirectTo']}`;
          } else {
          } */
          window.location.href = `${window.location.origin}/home`;
        }),
      );
  }
}
